import { MfaFactor } from '@logto/schemas';
import { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { sendMfaVerificationCode } from '@/apis/experience';
import useApi from '@/hooks/use-api';
import useErrorHandler from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useToast from '@/hooks/use-toast';
import { type VerificationCodeIdentifier } from '@/types';
import { type MfaFlowState } from '@/types/guard';
import { requestFreshCaptchaProof } from '@/utils/request-fresh-captcha';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

type Options = {
  /** Whether to replace the current page in the history stack on navigation. */
  replace?: boolean;
};

const useSendMfaVerificationCode = ({ replace }: Options = {}) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const navigate = useNavigateWithPreservedSearchParams();

  const handleError = useErrorHandler();
  const { t } = useTranslation();
  const { setToast } = useToast();
  const { executeCaptcha, isCaptchaRequired } = useContext(CaptchaContext);
  const asyncSendVerificationCode = useApi(sendMfaVerificationCode);
  const { setVerificationId } = useContext(UserInteractionContext);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const onSubmit = useCallback(
    async (identifier: VerificationCodeIdentifier, flowState: MfaFlowState) => {
      const captchaProof = await requestFreshCaptchaProof({
        isCaptchaRequired,
        executeCaptcha,
      });

      if (!captchaProof.token) {
        if (captchaProof.errorMessage) {
          setToast(captchaProof.errorMessage);
        }

        return;
      }

      const [error, result] = await asyncSendVerificationCode(identifier, captchaProof.token);

      if (error) {
        await handleError(error, {
          ...(identifier === 'phone' && {
            'request.rate_limited': () => {
              setToast(t('error.sms_rate_limited'));
            },
            'request.message_rate_limited': () => {
              setToast(t('error.sms_rate_limited'));
            },
          }),
        });

        return;
      }

      if (result) {
        // Store the verification ID in the context so that we can use it in the next step
        setVerificationId(codeVerificationTypeMap[identifier], result.verificationId);

        navigate(
          `/mfa-verification/${identifier === 'email' ? MfaFactor.EmailVerificationCode : MfaFactor.PhoneVerificationCode}`,
          { replace, state: flowState }
        );
      }
    },
    [
      asyncSendVerificationCode,
      executeCaptcha,
      handleError,
      isCaptchaRequired,
      navigate,
      replace,
      setToast,
      setVerificationId,
      t,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useSendMfaVerificationCode;
